/**
 * Paid ad-copy schemas — sibling of `schemas.ts`, same `VerbatimEvidence`
 * discipline: every claim the copy makes must cite a verbatim source string,
 * or it is not in the output.
 *
 * TWO SCHEMAS PER PLATFORM, ON PURPOSE (spec §5 item 3, the ⚠️ warning):
 * a **draft** schema the model generates against (loose: no character caps,
 * because none of `AD_SPECS`' numbers are confirmed hard limits — see
 * `src/constants/ad-specs.ts`) and a **strict output** schema the draft is
 * normalized into before anything downstream reads it (structural counts
 * enforced: exactly 2 primary texts, exactly 2 role-labelled headlines,
 * exactly 1 description). Reusing one schema for both jobs means a
 * constraint the model was never told about becomes a silent, billed,
 * nondeterministic failure — this repo has already lost runs that way.
 */

import { z } from 'zod';

const VerbatimEvidence = z
  .array(z.string().min(8))
  .min(1)
  .describe(
    'Verbatim sentences copied from the source (brand voice profile, product listing, canonical destination, or a real customer quote). Never paraphrased, never invented. A destination citation proves only where the click lands. Empty is not allowed — copy with zero evidence is a hypothesis and must be refused, not written.',
  );

const InternalReasoning = z
  .string()
  .describe('Why this angle/shape was chosen. Internal only — never rendered to the user.');

// --------------------------------------------------------------------------
// Meta
// --------------------------------------------------------------------------

export const MetaAdCopyDraftSchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  primaryTexts: z
    .array(
      z.object({
        text: z.string().min(10),
        shape: z
          .enum(['fuller', 'punchy'])
          .describe(
            'fuller = advertorial/story, reads past the fold. punchy = short, hook-only, built to work truncated. The two primary texts MUST use different shapes — that is the variant-shape rule, not two versions of the same line.',
          ),
      }),
    )
    .min(1),
  headlines: z
    .array(
      z.object({
        text: z.string().min(3),
        role: z
          .enum(['pain_restatement', 'outcome'])
          .describe(
            "pain_restatement = names the problem in the reader's words. outcome = names the transformation/result. Exactly one of each — never two of the same role.",
          ),
      }),
    )
    .min(1),
  description: z.string().min(3),
  cta: z
    .string()
    .min(2)
    .describe(
      'A short action phrase (e.g. "Shop Now", "Learn More"). Never the product name appended to the verb. This account has no approved-CTA list on file, so this field is ungoverned — flagged in the grounding disclosure, not enforced here.',
    ),
  evidence: VerbatimEvidence,
  reasoning: InternalReasoning,
});
export type MetaAdCopyDraft = z.infer<typeof MetaAdCopyDraftSchema>;

/**
 * `role` per position is an ENUM, not a per-position literal, because which
 * role ships FIRST is no longer fixed — see `leadHeadlineRoleForFramework`
 * (`ad-copy-doctrine.ts`, 2026-09-03): the assigned hook framework decides
 * it, so the schema must accept either order. `normalizeMetaAdCopy` below is
 * what still guarantees exactly one of each role — the schema alone only
 * constrains each SLOT to the two known roles, not their combination.
 */
const MetaHeadlineSchema = z.object({
  text: z.string(),
  role: z.enum(['pain_restatement', 'outcome']),
});

export const MetaAdCopyOutputSchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  primaryTexts: z
    .tuple([
      z.object({ text: z.string(), shape: z.enum(['fuller', 'punchy']) }),
      z.object({ text: z.string(), shape: z.enum(['fuller', 'punchy']) }),
    ])
    .describe('they-assert: creative-copy.md:15 "2 primary texts, 2 headlines, 1 description".'),
  headlines: z
    .tuple([MetaHeadlineSchema, MetaHeadlineSchema])
    .refine(([first, second]) => first.role !== second.role, {
      message: 'Headlines must include exactly one pain_restatement and one outcome headline.',
    }),
  description: z.string(),
  cta: z.string(),
  evidence: VerbatimEvidence,
  reasoning: InternalReasoning,
});
export type MetaAdCopyOutput = z.infer<typeof MetaAdCopyOutputSchema>;

// --------------------------------------------------------------------------
// Persisted Meta copy
// --------------------------------------------------------------------------

/**
 * The JSON stored in `ad_units.copy` is intentionally narrower than the
 * normalized model output above. Offer class, warnings, and model reasoning
 * have their own authoritative columns (or are internal-only) and must never
 * be duplicated inside the persisted copy payload.
 */
const PersistedMetaPrimaryTextSchema = z
  .object({
    text: z.string(),
    shape: z.enum(['fuller', 'punchy']),
  })
  .strict();

/**
 * `role` is an enum, not a per-slot literal — see `MetaHeadlineSchema` above
 * for why: which role ships in `headlines[0]` is decided by the assigned
 * hook framework (`leadHeadlineRoleForFramework`), not fixed to
 * pain-then-outcome. `PersistedMetaAdCopySchema` below still requires
 * exactly one of each role, in either order.
 */
const PersistedMetaHeadlineSchema = z
  .object({
    text: z.string(),
    role: z.enum(['pain_restatement', 'outcome']),
  })
  .strict();

export const PersistedMetaGroundingSchema = z
  .object({
    groundedIn: z.array(z.string()),
    notGroundedIn: z.array(z.string()),
    summary: z.string(),
  })
  .strict();
export type PersistedMetaGrounding = z.infer<typeof PersistedMetaGroundingSchema>;

export const PersistedAdCopyWarningSchema = z
  .object({
    field: z.string(),
    detail: z.string(),
    provenance: z.string(),
  })
  .strict();
export type PersistedAdCopyWarning = z.infer<typeof PersistedAdCopyWarningSchema>;

export const PersistedMetaAdCopySchema = z
  .object({
    primaryTexts: z
      .tuple([PersistedMetaPrimaryTextSchema, PersistedMetaPrimaryTextSchema])
      .refine(([first, second]) => first.shape !== second.shape, {
        message: 'Primary texts must use different shapes.',
      }),
    headlines: z
      .tuple([PersistedMetaHeadlineSchema, PersistedMetaHeadlineSchema])
      .refine(([first, second]) => first.role !== second.role, {
        message: 'Headlines must include exactly one pain_restatement and one outcome headline.',
      }),
    description: z.string(),
    cta: z.string(),
    evidence: z.array(z.string().min(8)).min(1),
  })
  .strict();
export type PersistedMetaAdCopy = z.infer<typeof PersistedMetaAdCopySchema>;

// --------------------------------------------------------------------------
// LinkedIn — field name `commentary` is UNVERIFIED-EXTERNAL (one in-session
// fetch of the Creatives API doc, zero repo corroboration) as the WIRE field
// name for the Creatives API. `description` is added here on firmer ground:
// LinkedIn's own "Single image ads advertising specifications" help page
// (verified 2026-08-11, see `AD_SPECS.linkedin.description`) documents intro
// text / headline / description as the three copy fields Sponsored Content
// ads carry, so the three-field shape is platform-doc even though the exact
// Creatives API JSON key for each remains unverified; see spec §3 and §6 Q5.
// --------------------------------------------------------------------------

export const LinkedInAdCopyDraftSchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  commentary: z.string().min(10),
  headline: z.string().min(3),
  description: z.string().min(3).optional(),
  destinationUrl: z.string().url().optional(),
  evidence: VerbatimEvidence,
  reasoning: InternalReasoning,
});
export type LinkedInAdCopyDraft = z.infer<typeof LinkedInAdCopyDraftSchema>;

export const LinkedInAdCopyOutputSchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  commentary: z.string(),
  headline: z.string(),
  description: z.string().optional(),
  destinationUrl: z.string().url().optional(),
  evidence: VerbatimEvidence,
  reasoning: InternalReasoning,
});
export type LinkedInAdCopyOutput = z.infer<typeof LinkedInAdCopyOutputSchema>;

export const PersistedLinkedInAdCopySchema = z
  .object({
    commentary: z.string(),
    headline: z.string(),
    description: z.string().optional(),
    destinationUrl: z.string().url().optional(),
    evidence: z.array(z.string().min(8)).min(1),
  })
  .strict();
export type PersistedLinkedInAdCopy = z.infer<typeof PersistedLinkedInAdCopySchema>;

// --------------------------------------------------------------------------
// Google RSA. Counts and character ceilings are verified against Google's
// official RSA/API documentation and mirrored in `AD_SPECS.google_rsa`.
//
// Unlike Meta and LinkedIn, an RSA is not a written ad — it is an INVENTORY of
// interchangeable assets that Google assembles per auction. That single fact
// drives the whole shape: assets carry a `role` so the set is forced to span
// real variety (a keyword mirror, an outcome, a proof, an offer, a call to
// action) instead of collapsing into fifteen rephrasings of one benefit, which
// gives the optimizer nothing to choose between.
// --------------------------------------------------------------------------

/**
 * Roles exist to make "variety" checkable rather than aspirational. Google
 * mixes any headline with any other two, so a set that is strong on paper but
 * says one thing five ways is a weak set, and nothing about its length reveals
 * that. The role tag is what lets `checkGoogleRsaAdContract` count distinct
 * jobs instead of counting strings.
 */
export const GOOGLE_RSA_HEADLINE_ROLES = [
  'keyword_mirror',
  'outcome',
  'proof',
  'offer',
  'cta_brand',
] as const;

export const GOOGLE_RSA_DESCRIPTION_ROLES = ['benefit', 'proof', 'offer', 'objection'] as const;

export const GoogleRsaAdCopyDraftSchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  headlines: z
    .array(
      z.object({
        text: z.string().min(3).max(30),
        role: z.enum(GOOGLE_RSA_HEADLINE_ROLES),
      }),
    )
    .min(3)
    .max(15),
  descriptions: z
    .array(
      z.object({
        text: z.string().min(3).max(90),
        role: z.enum(GOOGLE_RSA_DESCRIPTION_ROLES),
      }),
    )
    .min(2)
    .max(4),
  paths: z.array(z.string().min(1).max(15)).max(2),
  evidence: VerbatimEvidence,
  reasoning: InternalReasoning,
});
export type GoogleRsaAdCopyDraft = z.infer<typeof GoogleRsaAdCopyDraftSchema>;

export const GoogleRsaAdCopyOutputSchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  headlines: z.array(z.object({ text: z.string(), role: z.enum(GOOGLE_RSA_HEADLINE_ROLES) })),
  descriptions: z.array(z.object({ text: z.string(), role: z.enum(GOOGLE_RSA_DESCRIPTION_ROLES) })),
  paths: z.array(z.string()),
  evidence: VerbatimEvidence,
  reasoning: InternalReasoning,
});
export type GoogleRsaAdCopyOutput = z.infer<typeof GoogleRsaAdCopyOutputSchema>;

export const PersistedGoogleRsaAdCopySchema = z
  .object({
    headlines: z.array(z.object({ text: z.string(), role: z.string() }).strict()),
    descriptions: z.array(z.object({ text: z.string(), role: z.string() }).strict()),
    paths: z.array(z.string()),
    evidence: z.array(z.string().min(8)).min(1),
  })
  .strict();
export type PersistedGoogleRsaAdCopy = z.infer<typeof PersistedGoogleRsaAdCopySchema>;

// --------------------------------------------------------------------------
// READER schemas — what a stored row must contain for the UI to render it.
//
// DELIBERATELY SEPARATE FROM THE `Persisted*` SCHEMAS ABOVE, and deliberately
// NOT `.strict()`. Those describe what the writer emits TODAY; these describe
// what a reader can cope with, which is a strictly larger set because the
// writer has already changed once. Verified against prod on 2026-08-12: of 12
// `ad_units` rows, 10 (2026-08-09/10) carry `offerClass` and `warnings` INSIDE
// `copy` and 2 (2026-08-11+) do not, because warnings moved to their own
// column. `PersistedMetaAdCopySchema.strict()` rejects the first ten — so
// validating reads with the writer's schema would blank out most of the live
// data. Zod objects ignore unknown keys by default, which is exactly the
// tolerance a reader needs: both shapes parse, and a third shape would too.
//
// What these DO enforce is the part the UI actually dereferences. That is the
// point: `BriefConceptAdUnits` crashed on `copy.primaryTexts.map` for a
// LinkedIn row because nothing ever checked the shape matched the platform.
// --------------------------------------------------------------------------

/**
 * The concept copy-variant generator's own metadata, written INSIDE `copy`.
 *
 * Declared on every reader because Zod DROPS unknown keys: without it the tone
 * the generator reported reached the database and then evaporated on the way
 * back out, and the Content section could only ever render the counts. Optional
 * on purpose — the ten pre-existing rows have no `conceptCopy`, and an absent
 * tone must stay absent rather than become an invented label.
 */
const ConceptCopyMetaSchema = z
  .object({ tone: z.string().optional(), version: z.number().optional() })
  .optional();

export const MetaAdUnitCopyReadSchema = z.object({
  conceptCopy: ConceptCopyMetaSchema,
  primaryTexts: z.array(z.object({ text: z.string(), shape: z.enum(['fuller', 'punchy']) })).min(1),
  headlines: z
    .array(z.object({ text: z.string(), role: z.enum(['pain_restatement', 'outcome']) }))
    .min(1),
  description: z.string().default(''),
  cta: z.string().default(''),
  evidence: z.array(z.string()).default([]),
});

export const LinkedInAdUnitCopyReadSchema = z.object({
  conceptCopy: ConceptCopyMetaSchema,
  commentary: z.string(),
  headline: z.string(),
  description: z.string().optional(),
  evidence: z.array(z.string()).default([]),
});

export const GoogleRsaAdUnitCopyReadSchema = z.object({
  conceptCopy: ConceptCopyMetaSchema,
  headlines: z.array(z.object({ text: z.string(), role: z.string() })).min(1),
  descriptions: z.array(z.object({ text: z.string(), role: z.string() })).min(1),
  paths: z.array(z.string()).default([]),
  evidence: z.array(z.string()).default([]),
});

// --------------------------------------------------------------------------
// Normalizers — draft → strict output. Fill missing structural slots is NOT
// this function's job (that is what `repair-ad-copy.ts`'s repair round-trip
// is for); this only reshapes a draft that already has the right counts.
// --------------------------------------------------------------------------

export interface AdCopyNormalizeIssue {
  field: string;
  detail: string;
}

export interface NormalizeMetaAdCopyOptions {
  /**
   * Which role ships as `headlines[0]` — the slot `copy-suggestions-core.ts`
   * reads as THE headline (`ad.headlines[0]`). Defaults to `'pain_restatement'`,
   * the pre-existing behavior, so every caller that has not opted into
   * `leadHeadlineRoleForFramework` (`ad-copy-doctrine.ts`) is unaffected.
   */
  leadHeadlineRole?: 'pain_restatement' | 'outcome';
}

export function normalizeMetaAdCopy(
  draft: MetaAdCopyDraft,
  options: NormalizeMetaAdCopyOptions = {},
): {
  output: MetaAdCopyOutput | null;
  issues: AdCopyNormalizeIssue[];
} {
  const issues: AdCopyNormalizeIssue[] = [];

  if (draft.primaryTexts.length !== 2) {
    issues.push({
      field: 'primaryTexts',
      detail: `Expected exactly 2 primary texts, got ${draft.primaryTexts.length}.`,
    });
  } else if (draft.primaryTexts[0].shape === draft.primaryTexts[1].shape) {
    issues.push({
      field: 'primaryTexts',
      detail: `Both primary texts used the "${draft.primaryTexts[0].shape}" shape — the variant-shape rule requires one fuller and one punchy.`,
    });
  }

  const painHeadline = draft.headlines.find((h) => h.role === 'pain_restatement');
  const outcomeHeadline = draft.headlines.find((h) => h.role === 'outcome');
  if (!painHeadline || !outcomeHeadline || draft.headlines.length !== 2) {
    issues.push({
      field: 'headlines',
      detail: `Expected exactly one pain_restatement headline and one outcome headline, got ${draft.headlines.length} headline(s) with roles [${draft.headlines.map((h) => h.role).join(', ')}].`,
    });
  }

  if (issues.length > 0 || !painHeadline || !outcomeHeadline) {
    return { output: null, issues };
  }

  const leadHeadlineRole = options.leadHeadlineRole ?? 'pain_restatement';
  const painHeadlineOutput = { text: painHeadline.text, role: 'pain_restatement' as const };
  const outcomeHeadlineOutput = { text: outcomeHeadline.text, role: 'outcome' as const };

  return {
    output: {
      offerClass: draft.offerClass,
      primaryTexts: [draft.primaryTexts[0], draft.primaryTexts[1]],
      headlines:
        leadHeadlineRole === 'outcome'
          ? [outcomeHeadlineOutput, painHeadlineOutput]
          : [painHeadlineOutput, outcomeHeadlineOutput],
      description: draft.description,
      cta: draft.cta,
      evidence: draft.evidence,
      reasoning: draft.reasoning,
    },
    issues: [],
  };
}

/**
 * RSA normalization is mostly a DUPLICATE hunt, because the failure mode here
 * is not a missing field — the draft schema already guarantees counts and
 * lengths — it is fifteen assets that all say the same thing. Google assembles
 * a different combination every auction, so two headlines with identical text
 * (or a set covering only one role) quietly wastes most of the inventory while
 * looking complete.
 */
export function normalizeGoogleRsaAdCopy(draft: GoogleRsaAdCopyDraft): {
  output: GoogleRsaAdCopyOutput | null;
  issues: AdCopyNormalizeIssue[];
} {
  const issues: AdCopyNormalizeIssue[] = [];

  const seenHeadlines = new Set<string>();
  const dedupedHeadlines = draft.headlines.filter((headline) => {
    const key = headline.text.trim().toLowerCase();
    if (!key || seenHeadlines.has(key)) return false;
    seenHeadlines.add(key);
    return true;
  });
  if (dedupedHeadlines.length < 3) {
    issues.push({
      field: 'headlines',
      detail: `Only ${dedupedHeadlines.length} distinct headline(s) after removing duplicates; Google requires at least 3 that differ.`,
    });
  }

  const seenDescriptions = new Set<string>();
  const dedupedDescriptions = draft.descriptions.filter((description) => {
    const key = description.text.trim().toLowerCase();
    if (!key || seenDescriptions.has(key)) return false;
    seenDescriptions.add(key);
    return true;
  });
  if (dedupedDescriptions.length < 2) {
    issues.push({
      field: 'descriptions',
      detail: `Only ${dedupedDescriptions.length} distinct description(s) after removing duplicates; Google requires at least 2 that differ.`,
    });
  }

  const headlineRoles = new Set(dedupedHeadlines.map((headline) => headline.role));
  if (headlineRoles.size < 3) {
    issues.push({
      field: 'headlines',
      detail: `Headlines cover only ${headlineRoles.size} role(s) [${[...headlineRoles].join(', ')}]. An RSA needs at least 3 distinct roles or the optimizer has nothing to choose between.`,
    });
  }

  if (issues.length > 0) return { output: null, issues };

  return {
    output: {
      offerClass: draft.offerClass,
      headlines: dedupedHeadlines,
      descriptions: dedupedDescriptions,
      paths: draft.paths,
      evidence: draft.evidence,
      reasoning: draft.reasoning,
    },
    issues: [],
  };
}

export function normalizeLinkedInAdCopy(draft: LinkedInAdCopyDraft): {
  output: LinkedInAdCopyOutput | null;
  issues: AdCopyNormalizeIssue[];
} {
  const issues: AdCopyNormalizeIssue[] = [];

  if (!draft.commentary.trim()) {
    issues.push({ field: 'commentary', detail: 'Commentary (intro text) is empty.' });
  }
  if (!draft.headline.trim()) {
    issues.push({ field: 'headline', detail: 'Headline is empty.' });
  }

  if (issues.length > 0) {
    return { output: null, issues };
  }

  return { output: draft, issues: [] };
}
